// One choice out of a handful, all of them visible — difficulty, a pace, a
// board size. Replaces `ToggleButtonGroup`.
//
// **One bordered pill holding borderless buttons**, rather than a row of
// buttons with their edges collapsed: MUI's group spends its styling undoing
// the borders it drew, and the seam it leaves behind is the thing that never
// quite lines up. The selected segment is the only brand fill in the row —
// selection is what the accent is rationed to (PLAN-UI §28.2).
//
// `aria-pressed` per button, not a radiogroup: these are buttons that stay
// down, and every call site reads the value back off `onChange`.

import { styled } from "@mui/material/styles";
import type { ComponentProps, ReactNode } from "react";

import { motion } from "./motion";

export interface SegmentedOption<T> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
  testId?: string;
}

export interface SegmentedProps<T extends string | number>
  extends Omit<ComponentProps<"div">, "onChange"> {
  value: T;
  onChange: (value: T) => void;
  options: readonly SegmentedOption<T>[];
  small?: boolean;
}

const Row = styled("div")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "stretch",
  gap: 2,
  padding: 2,
  background: `color-mix(in srgb, ${theme.kit.color.panelRaised} 85%, transparent)`,
  border: `2px solid ${theme.kit.color.line}`,
  borderRadius: theme.kit.radius.pill
}));

const Seg = styled("button", {
  shouldForwardProp: (prop: string) => prop !== "on" && prop !== "small"
})<{ on?: boolean; small?: boolean }>(({ theme, on, small }) => {
  const c = theme.kit.color;
  return {
    border: 0,
    borderRadius: theme.kit.radius.pill,
    background: on ? c.brand : "transparent",
    color: on ? c.onBrand : c.ink,
    fontFamily: theme.kit.font.body,
    fontWeight: 700,
    fontSize: small ? 11.5 : 13,
    lineHeight: 1.2,
    padding: small ? "4px 11px" : "6px 15px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    ...motion([], ["background", "color"]),
    "&:hover": on
      ? {}
      : { background: `color-mix(in srgb, ${c.line} 45%, transparent)` },
    "&:focus-visible": { outline: `2px solid ${c.brand}`, outlineOffset: 2 },
    "&:disabled": { opacity: 0.38, pointerEvents: "none" },
    "@media (pointer: coarse)": { minHeight: theme.kit.touchTarget }
  };
});

export function Segmented<T extends string | number>({
  value,
  onChange,
  options,
  small,
  ...rest
}: SegmentedProps<T>) {
  return (
    <Row {...rest}>
      {options.map((option) => (
        <Seg
          key={String(option.value)}
          type="button"
          on={option.value === value}
          small={small}
          disabled={option.disabled}
          aria-pressed={option.value === value}
          data-testid={option.testId}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </Seg>
      ))}
    </Row>
  );
}
